import { readFile, stat } from "node:fs/promises";
import path from "node:path";
import {
  tryWithFsDeadline,
  walkDir,
  type WalkTruncation,
  withFsDeadline,
} from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import type {
  ConnectorRequirement,
  FileCheck,
  InputCheck,
  NodeOverrides,
  ReadCheck,
  RequirementsReport,
  ResourceCheck,
  SkillInputFile,
  SkillRequirements,
} from "./types.js";

let validatorLogger: ReturnType<typeof createLogger> | undefined;

function log(): ReturnType<typeof createLogger> {
  validatorLogger ??= createLogger({ kind: "runner", subkind: "skill-requirements" });
  return validatorLogger;
}

/** Best-effort existence probe. A missing path, an unreadable one, and an
 *  unresponsive mount are all "not usable here" for a requirement check. */
async function pathExists(target: string): Promise<boolean> {
  return tryWithFsDeadline(
    `stat ${target}`,
    async () => {
      await stat(target);
      return true;
    },
    false,
  );
}

/**
 * Validate skill requirements against the filesystem and collected inputs.
 *
 * File checks resolve paths relative to `projectDir`. Hard-gate files that are absent
 * set `satisfied: false` on the returned report; soft-gate misses are recorded but do not block.
 * Input checks read `_concept/_grounding/{skillId}/input.json`; missing files are treated as
 * no inputs collected. Resource checks are always recorded as `available: true` — actual
 * availability must be confirmed by the runner's `ResourceManager`.
 *
 * **Async and bounded since #454.** `projectDir` reaches this function straight
 * from the agent — `workspace_check_skill_requirements` takes `target_dir` as a
 * tool argument — so it can be `/skaile/workspace`, the rclone FUSE mount. Every
 * probe is now awaited and deadline-bounded, and the `min_entries` count walks
 * through the bounded {@link walkDir}, so a wedged mount or a symlink cycle
 * costs one failed tool call instead of the whole `skaile serve` event loop.
 *
 * @param skillId - Canonical skill identifier, used to locate `input.json`
 * @param requirements - Parsed requirements from SKILL.md (`parseSkillRequirements` output)
 * @param projectDir - Absolute path to the project root directory
 * @param overrides - Optional flow-level overrides; paths in `skip_checks` are treated as present
 * @param producesIndex - Maps file paths to the skill ID that produces them, enabling "produced by" hints in the report
 * @returns `RequirementsReport` with per-check results and an overall `satisfied` flag
 *
 * @docLink packages/resolver/api-reference#validate-requirements
 */
export async function validateRequirements(
  skillId: string,
  requirements: SkillRequirements,
  projectDir: string,
  overrides?: NodeOverrides,
  producesIndex?: Map<string, string>,
): Promise<RequirementsReport> {
  const skipSet = new Set(overrides?.skip_checks ?? []);

  // Check files. Serial on purpose: `projectDir` may be one network mount, and
  // fanning every probe out concurrently multiplies the queue depth on exactly
  // the resource that is already the bottleneck.
  const files: FileCheck[] = [];
  for (const req of requirements.files) {
    if (skipSet.has(req.path)) {
      files.push({ path: req.path, gate: req.gate, exists: true, description: req.description });
      continue;
    }

    const fullPath = path.join(projectDir, req.path);
    const check: FileCheck = {
      path: req.path,
      gate: req.gate,
      exists: false,
      description: req.description,
      producedBy: producesIndex?.get(req.path),
    };

    if (req.min_entries != null) {
      const counted = await checkDirectoryMinEntries(fullPath, req.min_entries);
      check.exists = counted.satisfied;
      if (counted.truncated) check.truncated = true;
    } else {
      check.exists = await pathExists(fullPath);
    }

    files.push(check);
  }

  // Check inputs — look for _concept/_grounding/{skillId}/input.json
  const inputFile = path.join(projectDir, "_concept", "_grounding", skillId, "input.json");
  let collectedValues: Record<string, any> = {};
  try {
    const raw = await withFsDeadline(`readFile ${inputFile}`, () => readFile(inputFile, "utf-8"));
    const parsed = JSON.parse(raw) as SkillInputFile;
    collectedValues = parsed.values ?? {};
  } catch {
    // No input file — all inputs are missing
  }

  const allInputs = [
    ...requirements.inputs_required.map((f) => ({ field: f, required: true })),
    ...requirements.inputs_optional.map((f) => ({ field: f, required: false })),
  ];

  const inputs: InputCheck[] = allInputs.map(({ field, required }) => ({
    id: field.id,
    required,
    present: field.id in collectedValues,
    field,
  }));

  // Check reads (informational only)
  const reads: ReadCheck[] = [];
  for (const r of requirements.reads) {
    reads.push({
      path: r.path,
      exists: await pathExists(path.join(projectDir, r.path)),
      description: r.description,
    });
  }

  // Check resources (validated at a higher level if a ResourceManager is available;
  // here we just record them in the report with unknown status)
  const resources: ResourceCheck[] = (requirements.resources ?? []).map((r) => ({
    id: r.id,
    available: true, // Actual availability checked by runner with ResourceManager
    missingOps: [],
    optional: r.optional ?? false,
  }));

  // Satisfaction: all hard file gates pass AND all required inputs present
  const hardFilesSatisfied = files.filter((f) => f.gate === "hard").every((f) => f.exists);

  const requiredInputsSatisfied = inputs.filter((i) => i.required).every((i) => i.present);

  const requiredResourcesSatisfied = resources.filter((r) => !r.optional).every((r) => r.available);

  return {
    skillId,
    satisfied: hardFilesSatisfied && requiredInputsSatisfied && requiredResourcesSatisfied,
    files,
    inputs,
    reads,
    resources,
  };
}

/**
 * A warning produced by `validateConnectorRequirements` when a skill's declared
 * connector requirement is not satisfied by the connectors in skaile.yaml.
 * Each warning covers either a missing driver or an insufficient access level.
 *
 * @docLink packages/resolver/api-reference#connector-validation-warning
 */
export interface ConnectorValidationWarning {
  /** Driver name that triggered the warning. */
  driver: string;
  /** Human-readable explanation of the gap, including a remediation command where applicable. */
  message: string;
}

/** Minimal structural type accepted by validateConnectorRequirements. Compatible with both
 *  ConnectorDeclaration shapes in @skaile/workspaces/core and @skaile/workspaces/connectors. */
interface AvailableConnector {
  driver: string;
  access?: "read-only" | "read-write";
}

/**
 * Check that all connector requirements declared in a skill's SKILL.md are
 * satisfied by the connectors declared in skaile.yaml.
 *
 * Returns one warning per gap — either a missing driver or an insufficient
 * access level. This is a pre-flight advisory; no runtime enforcement is done.
 *
 * @param requirements - Connector requirements from `parseSkillRequirements().connectors`
 * @param available - Connector declarations from `skaile.yaml` (via `loadConnectorDeclarations`)
 * @returns Array of warnings — one per gap. Empty array when all requirements are satisfied.
 *
 * @docLink packages/resolver/api-reference#validate-connector-requirements
 */
export function validateConnectorRequirements(
  requirements: ConnectorRequirement[],
  available: AvailableConnector[],
): ConnectorValidationWarning[] {
  const warnings: ConnectorValidationWarning[] = [];

  for (const req of requirements) {
    const matches = available.filter((a) => a.driver === req.driver);

    if (matches.length === 0) {
      warnings.push({
        driver: req.driver,
        message:
          `Skill requires connector driver "${req.driver}" but none is declared in skaile.yaml. ` +
          `Run: skaile connector add ${req.driver}`,
      });
      continue;
    }

    if (req.access === "read-write") {
      const hasReadWrite = matches.some((m) => (m.access ?? "read-only") === "read-write");
      if (!hasReadWrite) {
        warnings.push({
          driver: req.driver,
          message:
            `Skill requires "${req.driver}" with read-write access, but all declared instances ` +
            `are read-only. Update access in skaile.yaml.`,
        });
      }
    }
  }

  return warnings;
}

/**
 * Does `dirPath` hold at least `minEntries` files, counted recursively?
 *
 * `truncated` is set when a walk bound was hit *and* the count still fell
 * short: the number is then a floor, not the truth, so `satisfied: false` is
 * "we could not prove it" rather than "there are not enough files". Reaching
 * `minEntries` short-circuits the walk, so the common case never pays for the
 * whole tree and a bound hit after that point is irrelevant.
 */
async function checkDirectoryMinEntries(
  dirPath: string,
  minEntries: number,
): Promise<{ satisfied: boolean; truncated: boolean }> {
  const st = await tryWithFsDeadline(`stat ${dirPath}`, () => stat(dirPath), null);
  if (!st?.isDirectory()) return { satisfied: false, truncated: false };

  const { count, truncation } = await countFilesRecursive(dirPath, minEntries);
  const satisfied = count >= minEntries;
  if (!satisfied && truncation) {
    log().warn("min_entries count truncated — result is a lower bound, not the file count", {
      dirPath,
      minEntries,
      count,
      ...truncation,
    });
  }
  return { satisfied, truncated: !satisfied && truncation !== undefined };
}

/**
 * Count files under `dirPath`, stopping at `limit`.
 *
 * Delegates to core's {@link walkDir} rather than recursing here: `dirPath` is
 * agent-supplied and can be a network mount, so the walk needs the deadline per
 * `opendir`, the depth / entry / wall-clock bounds, and the visited-inode cycle
 * guard that walker already owns. The pre-#454 `readdirSync` recursion had none
 * of them and recursed forever on `a/link -> a`.
 *
 * Inheriting `walkDir` also inherits its skip list: `.git` and `node_modules`
 * no longer count toward `min_entries`, which is what a prerequisite like
 * "at least 3 feature files" meant in the first place.
 */
async function countFilesRecursive(
  dirPath: string,
  limit: number,
): Promise<{ count: number; truncation?: WalkTruncation }> {
  let count = 0;
  let truncation: WalkTruncation | undefined;
  for await (const _entry of walkDir(dirPath, {
    onTruncate: (info) => {
      truncation ??= info;
    },
  })) {
    count++;
    // Enough is enough — no reason to keep walking a large tree (or a wedged
    // mount) once the requirement is already met.
    if (count >= limit) return { count };
  }
  return { count, truncation };
}
